import { useEffect, useState } from "react";

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import AppCard from "@plannel/components/ui/AppCard";
import CardContent from '@mui/material/CardContent';
import MailLock from '@mui/icons-material/MailLock';
import LinearProgress from '@mui/material/LinearProgress';

import { withTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import i18next from "@plannel/utils/i18n";
import isEmail from "validator/lib/isEmail";
import { isEqual, startsWith } from "lodash";

import systemService from "@plannel/services/system/system-service"

import { RouteList } from "@plannel/routeList";
import Snackbar from "@plannel/components/Snackbar";
import PlannelFooter from "@plannel/components/PlannelFooter";
import SelectLanguage from "@plannel/components/SelectLanguage"
import BackToSignIn from "@plannel/components/BackToSignIn";
import logo from "@plannel/assets/icons/main-logo.png";
import debug from "@plannel/utils/debug-util";
import { FS } from "@plannel/utils/font-util";

const MailAuthentication = ({ t }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [tenant, setTenant] = useState("");
  const [form, setForm] = useState({ message: "" });
  const [errors, setErrors] = useState({});
  const [completed, setCompleted] = useState(false);

  const [onProgress, setOnProgress] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const handleResponse = () => {
    setCompleted(true);
    setForm({ message: "" });
  }

  const handleErrorResponse = (error, loginUser) => {
    const { data } = error.response;
    const errorMessage = data?.message ? data.message : "MSG_NetworkError";

    debug(error);
    setForm({
      ...form,
      ...data,
      errorUsername: loginUser,
      message: errorMessage
    });
  }

  // true/false
  const tenantAndEmailStartsWith = (compareUsername) => {
    const tenantSplit = tenant.split("-")[0];
    const emailSplit = compareUsername.split("@")[1];

    return startsWith(emailSplit, tenantSplit);
  }

  const findFormErrors = () => {
    const newErrors = {};

    if (!username || username === '') {
      newErrors.username = "MSG_FieldRequired";
    } else if (!isEmail(username)) {
      newErrors.username = "MSG_CheckUsername";
    } else if (tenant) {
      if (!tenantAndEmailStartsWith(username)) {
        newErrors.username = "MSG_CheckTenantAndEmailFormat";
      }
    }

    if (!tenant || tenant === '') {
      newErrors.tenant = "MSG_FieldRequired";
    } else if (errors?.tenant) {
      newErrors.tenant = errors.tenant;
    }

    return newErrors
  }

  const sendAuthenticationMail = () => {
    const newErrors = findFormErrors();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      setOnProgress(true);
      const loginUser = username;
      const verifyMailParams = { tenant, username };
      systemService.verifyMailAuthentication(verifyMailParams)
        .then(() => {
          handleResponse();
        })
        .catch(error => handleErrorResponse(error, loginUser))
        .finally(() => {
          setOnProgress(false);
        });
    }
  }

  const setErrorMessage = () => {
    if (i18next.exists(form.message)) {
      if (isEqual(form.message, "MSG_UsernameExist")) {
        return t(form.message, { username: form.errorUsername });
      }

      return t(form.message);
    }

    if(errors?.username) {
      return t(errors.username);
    } else {
      return t("MSG_NetworkError");
    }
  }

  const onBlurTenant = (e) => {
    if (e.target.value.length > 0) {
      systemService.verifyTenant(e.target.value)
      .then((response) => {
        const results = response.data;
        if (results?.message) {
          const returnErrors = { ...errors, tenant: results.message }
          setErrors(returnErrors);
        } else {
          if(isEmail(username)) {
            if (!tenantAndEmailStartsWith(username)) {
              setErrors({ ...errors, username: "MSG_CheckTenantAndEmailFormat", tenant: null });
            } else {
              setErrors({ ...errors, username: null, tenant: null });
            }
          }
        }
      })
      .catch(error => {
        debug(error);
        const { message } = error.response.data;
        setErrors({ ...errors, tenant: message });
      })
    }
  }

  const onChangeTenant = e => {
    setTenant(e.target.value);

    if (errors?.username) {
      setErrors({ ...errors, tenant: null });
    }
  }

  const onChangeUsername = e => {
    setUsername(e.target.value);

    if (errors?.username) {
      setErrors({ ...errors, username: null });
    }
  }

  const onBlurUsername = e => {
    const value = e.target.value;
    if (value?.length > 0) {
      if(!isEmail(value)) {
        setErrors({ ...errors, username: "MSG_CheckUsername" });
      } else {
        if (!tenantAndEmailStartsWith(value)) {
          setErrors({ ...errors, username: "MSG_CheckTenantAndEmailFormat" });
        } else {
          setErrors({ ...errors, username: null });
        }
      }
    }
  }

  useEffect(() => {
    if (!location?.state?.modifiable) {
      navigate(RouteList.Signin.path);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container id="app-home" component="main" maxWidth="xs">
      <CssBaseline />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }} >
        <Box mt={8}>
          <img src={logo} width={170} alt="loading..." />
        </Box>
        {
          completed ? "" : (
            <AppCard>
              <CardContent sx={{ pb: '16px !important', display: "flex", flexDirection: "column", alignItems: "center" }}>
                <MailLock />
                <Typography variant="h6" fontSize={FS.DISPLAY} my={1}>{t("emailAuthentication")}</Typography>
                <Typography variant="body2" sx={{ my: 1, mx: 0.5 }}>{t("MSG_PleaseCheckEmailAuthentication")}</Typography>
                <TextField
                  fullWidth
                  required
                  margin="normal"
                  id="tenant"
                  label={t("tenant")}
                  name="tenant"
                  autoComplete="tenant"
                  value={tenant}
                  error={Boolean(errors?.tenant)}
                  helperText={t(errors.tenant)}
                  onChange={onChangeTenant}
                  onBlur={onBlurTenant}
                />
                <TextField
                  fullWidth
                  required
                  margin="normal"
                  id="email"
                  label={t("emailAddress")}
                  name="email"
                  autoComplete="email"
                  value={username}
                  error={Boolean(errors?.username)}
                  helperText={t(errors?.username)}
                  onChange={onChangeUsername}
                  onBlur={onBlurUsername}
                />
                {onProgress ? <LinearProgress sx={{ width: "100%" }} /> : ""}
                <Button fullWidth sx={{ my: 1 }} variant="contained" disabled={onProgress} onClick={sendAuthenticationMail}>{t("confirm")}</Button>
              </CardContent>
            </AppCard>
          )
        }
      </Box>
      <Box sx={{ mt: 1 }}>
        {Boolean(form.message) && (
          <Alert sx={{ mb: 2 }} severity="error">{setErrorMessage()}</Alert>
        )}
        {completed ? <Alert severity="success">{t("MSG_SendAthenticationLink")}</Alert> : ""}
        <Box sx={{ mt: 1, display: "flex" }}>
          <SelectLanguage />
          <BackToSignIn />
        </Box>
      </Box>
      <PlannelFooter/>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </Container>
  );
};

export default withTranslation()(MailAuthentication);